import {
  Alert,
  Box,
  Card,
  Dialog,
  Grid,
  Tab,
  DialogActions,
  GridProps,
  DialogContent,
  DialogTitle,
  Button,
  styled,
} from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { ModalList } from "src/components/DialogModal/DialogModalComponents";
import { ManageAircraft } from "src/components/Admin/Tabs/Aircraft/ManageAircraft";
import { ManageAircraftGroup } from "src/components/Admin/Tabs/AircraftGroup/ManageAircraftGroups";
import { ManageAirports } from "src/components/Admin/Tabs/Airports/ManageAirports";
import { ManageCrew } from "src/components/Admin/Tabs/Crew/ManageCrew";
import { ManageCrewDevices } from "src/components/Admin/Tabs/CrewDevices/ManageCrewDevices";
import { ManageUserDevices } from "src/components/Admin/Tabs/UserDevices/ManageUserDevices";
import { ManageCustomers } from "src/components/Admin/Tabs/Customers/ManageCustomers";
import { ManageUsers } from "src/components/Admin/Tabs/Users/ManageUsers";
import { OcTabs } from "src/components/OcTabs/OcTabs";
// Style Imports
import {} from "@mui/material";
import { useTheme } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { ManageAPIS } from "src/components/Admin/Tabs/APIS/ManageAPIS";
import { ManageCrewSchedule } from "src/components/Admin/Tabs/CrewSchedule/ManageCrewSchedule";
import { ManageDispatchDesks } from "src/components/Admin/Tabs/DispatchDesks/ManageDispatchDesks";
import { AdminHome } from "src/components/Admin/Tabs/Home/AdminHome";
import { ManageGenDec } from "src/components/Admin/Tabs/GenDecManagement/ManageGenDec";
import { loadGendecConfig } from "src/redux/actions/Gendec/Gendec.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

// TODO Get permission tied to all tabs.
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";

interface IAdminWindowProps {
  id: string;
  path: string[];
}

export interface IActiveModal {
  title: string;
  message: string;
  cancel: () => void;
  confirm: () => void;
  confirmLabel: string;
  /**
   * Items the action will affect, listed under the message so an admin can see
   * exactly what is about to change before confirming.
   */
  list?: string[];
  /**
   * Optional middle action, for when there is a safer alternative worth offering
   * at the point of confirmation (e.g. "Deactivate instead" on a bulk removal).
   */
  secondary?: {
    label: string;
    action: () => void;
  };
  /** Styles the confirm button as destructive, for irreversible actions. */
  destructive?: boolean;
}

interface ITabPermissions {
  AdminHomeTab: boolean;
  AircraftTab: boolean;
  AircraftGroupsTab: boolean;
  AirportsTab: boolean;
  PolicyTab: boolean;
  AppSettingsTab: boolean;
  EventsTab: boolean;
  CustomersTab: boolean;
  OperatorTab: boolean;
  ManageFlight: boolean;
  ManageUsers: boolean;
  ManageCrew: boolean;
  ManageAPIS: boolean;
  ManageCrewSchedule: boolean;
  ManageCrewDevices: boolean;
  ManageUserDevices: boolean;
  ManageDispatchDesks: boolean;
  ManageGenDecs: boolean;
}

const defaultTabPermission: ITabPermissions = {
  AdminHomeTab: true,
  AircraftTab: false,
  AircraftGroupsTab: false,
  AirportsTab: false,
  PolicyTab: false,
  AppSettingsTab: false,
  EventsTab: false,
  CustomersTab: false,
  OperatorTab: false,
  ManageFlight: false,
  ManageUsers: false,
  ManageCrew: false,
  ManageAPIS: false,
  ManageCrewSchedule: false,
  ManageCrewDevices: false,
  ManageUserDevices: false,
  ManageDispatchDesks: false,
  ManageGenDecs: false,
};

const AdminStyledGrid: React.FC<GridProps> = styled(Grid)(({ theme }) => ({
  width: "100%",
  minWidth: 700,
  overflowY: "auto",
  flexWrap: "nowrap",
  "&.horizontal": { marginRight: 10 },
  "& .admin_cards": {
    height: "100%",
    backgroundColor: theme.custom.timeline.contextListBackground,
    borderRadius: "0px 4px 4px 4px",
    "&.horizontal": {
      height: "calc(100% - 10px)",
    },
    "&.vertical": {
      marginRight: 10,
    },
  },
}));

export const AdminWindow = (props: IAdminWindowProps): JSX.Element => {
  const { id } = props;

  const contentRef = useRef<HTMLDivElement>(document.createElement("div"));
  const currentContentRef = contentRef?.current;
  const height = useMemo(() => currentContentRef?.getBoundingClientRect()?.height ?? 400, [currentContentRef]);

  // for OCTab
  const [tabValue, setTabValue] = useState<string>("AdminHomeTab");
  const [activeModal, setActiveModal] = useState<IActiveModal | null>(null);
  const isAPISCustomer = useSelector(
    (state: IRootState) =>
      import.meta.env.MODE === "development" ||
      state.structures.customerStructure.globalAppConfig?.customer?.settings?.isAPISEnabled
  );

  // Permissions Checks
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const isAdmin = useMemo(() => {
    if (policies) {
      return policies.findIndex((pol: any) => pol.policyName === "Admin") > -1;
    } else {
      return false;
    }
  }, [policies]);

  // The Gendecs plugin's tab/card requires Create + Update on the Gendec resource. Memo
  // the check so both the probe gate and `tabPermissions.ManageGenDecs` use a single
  // source of truth.
  const canManageGendecs = useMemo(
    () =>
      hasPermission(policies, PolicyResource.Gendec, PolicyAction.Create) &&
      hasPermission(policies, PolicyResource.Gendec, PolicyAction.Update),
    [policies]
  );

  // The plugin (and its /gendec/config endpoints) is only deployed for some customers.
  // We probe to find out, but only for users who could actually use the tab
  const dispatch = useDispatch();
  const gendecLoaded = useSelector((state: IRootState) => state.gendec?.loaded ?? false);
  const gendecConfig = useSelector((state: IRootState) => state.gendec?.config ?? null);
  useEffect(() => {
    if (canManageGendecs && !gendecLoaded) {
      void dispatch(loadGendecConfig());
    }
  }, [canManageGendecs, gendecLoaded, dispatch]);
  const gendecAvailable = gendecLoaded && gendecConfig !== null;

  const tabPermissions: ITabPermissions = useMemo(() => {
    return {
      ...defaultTabPermission,
      ...{
        AircraftTab:
          hasPermission(policies, PolicyResource.Aircraft, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.Aircraft, PolicyAction.Update) &&
          hasPermission(policies, PolicyResource.AircraftConfiguration, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.AircraftConfiguration, PolicyAction.Update),
        AircraftGroupsTab:
          hasPermission(policies, PolicyResource.Aircraft, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.Aircraft, PolicyAction.Update),
        AirportsTab:
          hasPermission(policies, PolicyResource.Airport, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.Airport, PolicyAction.Update),
        CustomersTab:
          //SystemAdmin is mainly customer related
          hasPermission(policies, PolicyResource.Customer, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.Customer, PolicyAction.Update),
        ManageUsers:
          hasPermission(policies, PolicyResource.User, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.User, PolicyAction.Update),
        ManageCrew:
          //this tab's endpoint are User endpoints
          hasPermission(policies, PolicyResource.CrewMember, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.CrewMember, PolicyAction.Update),
        ManageCrewSchedule: hasPermission(policies, PolicyResource.CrewMember, PolicyAction.Release),
        ManageAPIS:
          hasPermission(policies, PolicyResource.APIS, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.APIS, PolicyAction.Update),
        ManageCrewDevices: hasPermission(policies, PolicyResource.CrewMember, PolicyAction.Update),
        ManageUserDevices: hasPermission(policies, PolicyResource.CrewMember, PolicyAction.Update),
        ManageDispatchDesks:
          hasPermission(policies, PolicyResource.SystemAdmin, PolicyAction.Create) &&
          hasPermission(policies, PolicyResource.SystemAdmin, PolicyAction.Update),
        ManageGenDecs: canManageGendecs,
      },
    };
  }, [policies, canManageGendecs]);

  const layout = "horizontal";
  const gridPadding = "0px 10px";

  const handleSelectTab = (event: any, newValue: string): void => {
    setTabValue(newValue);
  };

  const tabContent = (): JSX.Element => {
    if (!!tabValue && typeof tabValue === "string" && tabPermissions[tabValue as keyof typeof tabPermissions]) {
      switch (tabValue) {
        case "AdminHomeTab":
          return (
            <AdminHome
              id={id}
              setActiveModal={setActiveModal}
              permissions={tabPermissions}
              setTabValue={setTabValue}
              gendecAvailable={gendecAvailable}
            />
          );
        case "AircraftTab":
          return <ManageAircraft id={id} setActiveModal={setActiveModal} height={height} />;
        case "AircraftGroupsTab":
          return <ManageAircraftGroup id={id} setActiveModal={setActiveModal} height={height} />;
        case "AirportsTab":
          return <ManageAirports id={id} setActiveModal={setActiveModal} height={height} />;
        case "CustomersTab":
          return <ManageCustomers id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageUsers":
          return <ManageUsers id={id} setActiveModal={setActiveModal} />;
        case "ManageCrew":
          return <ManageCrew id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageAPIS":
          return <ManageAPIS id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageCrewSchedule":
          return <ManageCrewSchedule id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageCrewDevices":
          return <ManageCrewDevices id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageUserDevices":
          return <ManageUserDevices id={id} setActiveModal={setActiveModal} />;
        case "ManageDispatchDesks":
          return <ManageDispatchDesks id={id} setActiveModal={setActiveModal} height={height} />;
        case "ManageGenDecs":
          return <ManageGenDec id={id} setActiveModal={setActiveModal} height={height} />;
        case "EMPTY":
          return <div></div>;
        default:
          return (
            <AdminHome
              id={id}
              setActiveModal={setActiveModal}
              permissions={tabPermissions}
              setTabValue={setTabValue}
              gendecAvailable={gendecAvailable}
            />
          );
      }
    }
    return <div></div>;
  };

  return (
    <AdminStyledGrid container direction={layout === "horizontal" ? "column" : "row"} wrap="nowrap">
      <Grid item style={{ height: 34 }}>
        <OcTabs
          variant="scrollable"
          orientation={layout}
          scrollButtons="auto"
          value={tabValue}
          onChange={handleSelectTab}
        >
          <Tab wrapped={true} value="AdminHomeTab" label="Admin" classes={{ root: "form-tab" }} />
          <Tab
            wrapped={true}
            value="AircraftTab"
            label="Aircraft"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["AircraftTab"]}
          />
          <Tab
            wrapped={true}
            value="AircraftGroupsTab"
            label="Aircraft Groups"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["AircraftGroupsTab"]}
          />
          <Tab
            wrapped={true}
            value="AirportsTab"
            label="Airports"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["AirportsTab"]}
          />
          <Tab
            wrapped={true}
            value="CustomersTab"
            label="Customers"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["CustomersTab"]}
          />

          <Tab
            wrapped={true}
            value="ManageUsers"
            label="Users"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageUsers"]}
          />
          <Tab
            wrapped={true}
            value="ManageCrew"
            label="Crew"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageCrew"]}
          />

          <Tab
            wrapped={true}
            value="ManageCrewSchedule"
            label="Crew Schedule"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageCrewSchedule"]}
          />
          <Tab
            wrapped={true}
            value="ManageCrewDevices"
            label="Crew Devices"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageCrewDevices"]}
          />
          <Tab
            wrapped={true}
            value="ManageUserDevices"
            label="User Devices"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageUserDevices"]}
          />
          <Tab
            wrapped={true}
            value="ManageDispatchDesks"
            label="Dispatch Desks"
            classes={{ root: "form-tab" }}
            disabled={!tabPermissions["ManageDispatchDesks"]}
          />
          {isAPISCustomer && (
            <Tab
              wrapped={true}
              value="ManageAPIS"
              label="APIS Parties"
              classes={{ root: "form-tab" }}
              disabled={!tabPermissions["ManageAPIS"]}
            />
          )}
          {gendecAvailable && (
            <Tab
              wrapped={true}
              value="ManageGenDecs"
              label="GenDec"
              classes={{ root: "form-tab" }}
              disabled={!tabPermissions["ManageGenDecs"]}
            />
          )}
        </OcTabs>
      </Grid>

      <Grid item style={{ height: "calc(100% - 34px)" }} ref={contentRef}>
        <Card className={`admin_cards ${layout}`}>{tabContent()}</Card>
      </Grid>

      {activeModal && (
        // Width is widened only for the richer confirmations that carry a list
        // of affected items. Every pre-existing caller keeps the dialog exactly
        // as it was - this PR should not restyle 18 admin confirmations on its
        // way past.
        <Dialog open={true} maxWidth={activeModal.list?.length ? "sm" : false} fullWidth={!!activeModal.list?.length}>
          <DialogTitle>{activeModal.title}</DialogTitle>
          <DialogContent>
            {activeModal.destructive ? (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                {activeModal.message}
              </Alert>
            ) : (
              activeModal.message
            )}
            {!!activeModal.list?.length && (
              // A long selection must not push the buttons off-screen.
              <Box sx={{ maxHeight: 240, overflowY: "auto", mt: 1 }}>
                <ModalList descrption={`${activeModal.list.length} affected:`} list={activeModal.list} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {/* A plain Cancel only where the confirm button is itself red or
                there is a middle option - three coloured buttons in a row reads
                as three equal choices. Otherwise the original styling stands. */}
            <Button
              variant={activeModal.destructive || activeModal.secondary ? "text" : "contained"}
              color={activeModal.destructive || activeModal.secondary ? "inherit" : "error"}
              size="small"
              onClick={activeModal.cancel}
            >
              Cancel
            </Button>
            {activeModal.secondary && (
              <Button variant="contained" color="warning" size="small" onClick={activeModal.secondary.action}>
                {activeModal.secondary.label}
              </Button>
            )}
            <Button
              variant="contained"
              color={activeModal.destructive ? "error" : "success"}
              size="small"
              onClick={activeModal.confirm}
            >
              {activeModal?.confirmLabel ?? "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </AdminStyledGrid>
  );
};
